type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LoggerConfig {
  prefix: string
  enabled: boolean
  level: LogLevel
}

class Logger {
  private config: LoggerConfig

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      prefix: '[kwry]:',
      enabled: true,
      level: 'debug',
      ...config
    }
  }

  private formatMessage(level: LogLevel, ...args: any[]): any[] {
    const timestamp = new Date().toISOString().slice(11, 23) // HH:mm:ss.SSS
    return [`${this.config.prefix} [${timestamp}] [${level.toUpperCase()}]`, ...args]
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false
    
    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3
    }
    
    return levels[level] >= levels[this.config.level]
  }

  debug(...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.debug(...this.formatMessage('debug', ...args))
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog('info')) {
      console.info(...this.formatMessage('info', ...args))
    }
  }

  log(...args: any[]): void {
    this.info(...args)
  }

  warn(...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(...this.formatMessage('warn', ...args))
    }
  }

  error(...args: any[]): void {
    if (this.shouldLog('error')) {
      console.error(...this.formatMessage('error', ...args))
    }
  }

  // Context-specific loggers for different parts of the app
  createContext(context: string): Logger {
    return new Logger({
      ...this.config,
      prefix: `${this.config.prefix} [${context}]`
    })
  }

  // Configuration methods
  setLevel(level: LogLevel): void {
    this.config.level = level
  }

  enable(): void {
    this.config.enabled = true
  }

  disable(): void {
    this.config.enabled = false
  }
}

// Create default logger instance
export const logger = new Logger()

// Create context-specific loggers for common modules
export const widgetLogger = logger.createContext('WIDGET')
export const embedLogger = logger.createContext('EMBED')
export const chatLogger = logger.createContext('CHAT')
export const apiLogger = logger.createContext('API')
export const hookLogger = logger.createContext('HOOK')
export const serviceLogger = logger.createContext('SERVICE')
export const shadowLogger = logger.createContext('SHADOW')

// Export Logger class for custom contexts
export { Logger }
export type { LogLevel, LoggerConfig } 